/**
 * Level 100's boss on screen (spec 02 §9.4): Super Drunk, placed from its 3x3-sprite box, walking
 * as it bounces; its robe light red once it has recovered angry (banded too with the colour aid);
 * reeling while stunned, and blinking out in the last ticks of the stun, as the C64 blinks it (held
 * in view with reduce flashing: the reel shows the stun). Also the ending's boss, pacing back and forth.
 */

import { type Material, Mesh, type Scene } from 'three';
import type { BossLook } from '../../sim/monsters';
import { buildBoss } from '../actors/boss';
import type { Puppet } from '../actors/puppet';
import type { Palette } from '../palette';
import type { FigureOptions } from './entityViews';
import { lerpCoord, pixelToWorld } from './simToWorld';

/** The boss's box is three sprites across and down (72 x 63 px); VIC (x, y) is its top-left. */
const BOX_W = 72;
const BOX_H = 63;

/** Where the boss's feet are, in playfield pixels: the bottom middle of its box (sprites sit 24, 50 in). */
export const bossFeet = (x: number, y: number): { x: number; y: number } => ({ x: x - 24 + BOX_W / 2, y: y - 50 + BOX_H });

/** Walk and bob the boss's parts (whatever of them its model has). */
export function poseBoss(pup: Puppet, t: number, speed: number): void {
  const ph = t * Math.PI * 2 * speed;
  if (pup.parts.has('footNear')) pup.pose('footNear', Math.sin(ph) * 0.5, Math.max(0, Math.cos(ph)) * 0.25);
  if (pup.parts.has('footFar')) pup.pose('footFar', -Math.sin(ph) * 0.5, Math.max(0, -Math.cos(ph)) * 0.25);
  if (pup.parts.has('body')) pup.pose('body', 0, Math.abs(Math.sin(ph)) * 0.15);
  if (pup.parts.has('armNear')) pup.pose('armNear', 0, Math.sin(ph) * 0.2);
  if (pup.parts.has('armFar')) pup.pose('armFar', 0, -Math.sin(ph) * 0.2);
}

export class BossView {
  private pup: Puppet | null = null;
  private angry = false;
  private dressed: Material | null = null;
  options: FigureOptions = { colourAid: false, reduceFlashing: false };

  /** `banded`: the colour aid's material for the angry boss (materials/aid.ts). */
  constructor(
    private readonly scene: Scene, private readonly p: Palette, private readonly material: Material,
    private readonly banded: Material = material,
  ) {}

  private puppet(angry: boolean): Puppet {
    if (!this.pup || this.angry !== angry) {
      this.clear();
      this.pup = buildBoss(this.p, this.material, angry);
      this.angry = angry;
      this.dressed = this.material;
      this.scene.add(this.pup.root);
    }
    const wear = angry && this.options.colourAid ? this.banded : this.material;
    if (wear !== this.dressed) {
      this.pup.root.traverse((o) => {
        if (o instanceof Mesh) o.material = wear;
      });
      this.dressed = wear;
    }
    return this.pup;
  }

  /**
   * `boss`: the MONSTER stream's `bossLook` (C64 colour 10 once angry); `before`: its position at the
   * previous sim frame (for gliding), else its own.
   */
  sync(boss: BossLook | null, before: { x: number; y: number } | undefined, alpha: number, t: number): void {
    if (!boss) return this.clear();
    const pup = this.puppet(boss.colour === 10);
    const from = before ?? boss;
    const f = bossFeet(lerpCoord(from.x, boss.x, alpha), lerpCoord(from.y, boss.y, alpha));
    pixelToWorld(f.x, f.y, pup.root.position);
    pup.root.visible = boss.visible || (this.options.reduceFlashing && boss.stunned);
    pup.turnTo(boss.facing, t);
    if (!boss.stunned) {
      pup.root.rotation.z = 0;
      poseBoss(pup, t, boss.colour === 10 ? 3 : 1.6);
    } else {
      // Stunned (or beaten): it reels on the spot.
      pup.root.rotation.z = Math.sin(t * 6) * 0.12;
      poseBoss(pup, t, 0.3);
    }
  }

  clear(): void {
    if (!this.pup) return;
    this.scene.remove(this.pup.root);
    this.pup.dispose();
    this.pup = null;
  }
}
